import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type FormDefinition } from "@njbsoft/form-logic";
import { parseFormDefinition } from "@njbsoft/form-renderer";
import { useFormTemplate, useTemplateVersions } from "@/hooks/use-company-form-template";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import { BuilderShell } from "@/components/form-builder/builder-shell";
import { Breadcrumbs, Card, PageHeader, PageSkeleton } from "@/components/ui";

export const Route = createFileRoute("/_company/company/forms/$templateId/builder")({
  component: TemplateBuilderPage,
});

/** Route-guard banner for direct navigation that must not mount the editor. */
function GuardBanner({ templateId, message }: { templateId: string; message: string }) {
  const { t } = useTranslation(["form-templates"]);
  return (
    <Card>
      <p className="text-sm text-on-surface">{message}</p>
      <Link
        to="/company/forms/$templateId"
        params={{ templateId }}
        className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
      >
        {t("builder.backToDetail")}
      </Link>
    </Card>
  );
}

function TemplateBuilderPage() {
  const { t } = useTranslation(["form-templates", "company"]);
  const { templateId } = Route.useParams();
  const isAdmin = useIsCompanyAdmin();

  const templateQuery = useFormTemplate(templateId);
  const versionsQuery = useTemplateVersions(templateId);

  if (templateQuery.isLoading || versionsQuery.isLoading) {
    return (
      <div className="mx-auto max-w-7xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  // Authoring a supplement writes the draft, and every company write arm
  // (ftv_company_insert / ftv_company_update) gates on the admin role — a
  // tester's autosave would answer 403 not_company_admin, so the editor
  // never mounts for one.
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-7xl p-8">
        <GuardBanner templateId={templateId} message={t("builder.guard.companyAdminOnly")} />
      </div>
    );
  }

  const template = templateQuery.data;
  if (templateQuery.isError || !template || versionsQuery.isError) {
    return (
      <div className="mx-auto max-w-7xl p-8">
        <Breadcrumbs
          items={[{ label: t("forms.title", { ns: "company" }), to: "/company/forms" }]}
        />
        <PageHeader title={t("company:forms.loadDetailFailed")} />
      </div>
    );
  }

  const breadcrumbs = (
    <Breadcrumbs
      items={[
        { label: t("forms.title", { ns: "company" }), to: "/company/forms" },
        { label: template.title, to: `/company/forms/${templateId}` },
        { label: t("builder.title") },
      ]}
    />
  );

  const versions = versionsQuery.data?.items ?? [];
  const draft = versions.find((v) => v.status === "draft");
  const latestPublished = versions
    .filter((v) => v.status === "published")
    .sort((a, b) => b.version - a.version)[0];

  // The reducer seeds from the server draft row; with no draft yet, the latest
  // published document is the "new draft" starting point (the first autosave
  // PUT creates the draft row — one-draft create-if-absent).
  const seedVersion = draft ?? latestPublished;
  const seedDefinition: FormDefinition = seedVersion
    ? // Drafts and publishes are written from this document model, so the wire
      // object narrows through the shared bridge; the `sections` shape is
      // confirmed by the guard below.
      parseFormDefinition(seedVersion.definition)
    : {
        schema_version: 1,
        target: template.target_kind === "device_test" ? "device" : "premise",
        sections: [],
      };

  // The bridge narrows, it does not validate: a draft saved without sections
  // arrives typed and unrenderable, so guard before mounting the editor.
  if (!Array.isArray(seedDefinition.sections)) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-8">
        {breadcrumbs}
        <PageHeader title={t("builder.title")} />
        <GuardBanner templateId={templateId} message={t("builder.guard.unrenderable")} />
      </div>
    );
  }

  // The editor branch alone is full-bleed and viewport-bound: the shell owns
  // the breadcrumbs + sticky header, and each pane scrolls independently.
  return (
    <div className="flex h-full min-h-0 flex-col">
      <BuilderShell
        // Remount (and reseed the reducer) when the backing draft row changes.
        key={seedVersion?.id ?? "new-draft"}
        templateId={templateId}
        templateTitle={template.title}
        deviceCategory={template.device_category ?? null}
        seedDefinition={seedDefinition}
        seedReviewRequired={seedVersion?.review_required}
      />
    </div>
  );
}
